'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  createTemplate,
  markTemplateDefault,
  deleteTemplate,
  getTemplate,
  listTemplates,
  updateTemplate,
  type AddToReportSeed,
  type DraftReport,
  type ReportTemplate,
  type TemplateSummary,
} from '../lib/templates';
import { askLive, type ModelRef } from '../lib/api';
import { DashboardView } from './DashboardView';

type BuilderInit = { id?: string; name: string; reports: DraftReport[] };
type Mode = { kind: 'list' } | { kind: 'builder'; init: BuilderInit } | { kind: 'dashboard'; template: ReportTemplate };

/**
 * Report Templates tab (§ Report Templates plan). Lists the tenant's saved templates
 * with View / Edit / Delete, a builder to create/update one (name + report questions),
 * and the dashboard View. `seed` lets "Ask AI" drop a question straight into a new template.
 */
export function ReportTemplates({
  modelRef,
  seed,
  onSeedConsumed,
  onDefaultChanged,
}: {
  modelRef: ModelRef;
  seed?: AddToReportSeed | null;
  onSeedConsumed?: () => void;
  /** Fired when the default moves, so the shell can refresh its Dashboard tab. */
  onDefaultChanged?: () => void;
}): React.JSX.Element {
  const [list, setList] = useState<TemplateSummary[]>([]);
  const [mode, setMode] = useState<Mode>({ kind: 'list' });
  const [err, setErr] = useState<string | null>(null);

  const refresh = () => listTemplates().then(setList).catch((e) => setErr((e as Error).message));
  useEffect(() => {
    void refresh();
  }, []);

  // A question added from Ask AI → open the builder in edit mode. Without a target
  // it starts a new template seeded with the question; with one, that template is
  // loaded (its items kept) and the question appended.
  useEffect(() => {
    if (!seed) return;
    let cancelled = false;
    void (async () => {
      const target = seed.templateId ? await getTemplate(seed.templateId).catch(() => null) : null;
      if (cancelled) return;
      const init: BuilderInit = target
        ? { id: target.id, name: target.name, reports: [...target.reports, seed.report] }
        : { name: '', reports: [seed.report] };
      setMode({ kind: 'builder', init });
      onSeedConsumed?.();
    })();
    return () => { cancelled = true; };
  }, [seed, onSeedConsumed]);

  const openView = async (id: string) => {
    const t = await getTemplate(id);
    if (t) setMode({ kind: 'dashboard', template: t });
  };
  const openEdit = async (id: string) => {
    const t = await getTemplate(id);
    if (t) setMode({ kind: 'builder', init: { id: t.id, name: t.name, reports: t.reports } });
  };
  const markDefault = async (id: string) => {
    await markTemplateDefault(id);
    await refresh();       // one row gains the badge, the previous one loses it
    onDefaultChanged?.();  // the shell re-reads which dashboard to land on
  };
  const remove = async (id: string, name: string) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    await deleteTemplate(id).catch((e) => setErr((e as Error).message));
    await refresh();
  };

  if (mode.kind === 'dashboard') {
    return <DashboardView template={mode.template} modelRef={modelRef} onBack={() => setMode({ kind: 'list' })} />;
  }
  if (mode.kind === 'builder') {
    return (
      <TemplateBuilder
        init={mode.init}
        modelRef={modelRef}
        onCancel={() => setMode({ kind: 'list' })}
        onSaved={async () => {
          await refresh();
          onDefaultChanged?.(); // the first template claims the dashboard — re-read it
          setMode({ kind: 'list' });
        }}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Report templates</h3>
        <button type="button" onClick={() => setMode({ kind: 'builder', init: { name: '', reports: [{ title: '', question: '' }] } })} style={primaryBtn}>
          + New template
        </button>
      </div>
      {err ? <p style={{ color: 'var(--bad, #dc2626)', fontSize: 13 }}>{err}</p> : null}
      {list.length === 0 ? (
        <p className="muted" style={{ fontSize: 14 }}>No templates yet. Create one, or add questions from the Ask tab.</p>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {list.map((t) => (
            <div key={t.id} className="card" style={rowStyle}>
              <div>
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {t.name}
                  {t.isDefault ? <span style={defaultBadge}>★ Default dashboard</span> : null}
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {t.reportCount} report{t.reportCount === 1 ? '' : 's'} · updated {new Date(t.updatedAt).toLocaleDateString()}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {t.isDefault ? null : (
                  <button type="button" onClick={() => void markDefault(t.id)} style={ghostBtn} title="Open this dashboard on sign-in">
                    ☆ Make default
                  </button>
                )}
                <button type="button" onClick={() => void openView(t.id)} style={primaryBtn}>View</button>
                <button type="button" onClick={() => void openEdit(t.id)} style={ghostBtn}>Edit</button>
                <button type="button" onClick={() => void remove(t.id, t.name)} style={dangerBtn}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface BuilderFailure { index: number; title: string; question: string; reason: string }

function TemplateBuilder({ init, modelRef, onCancel, onSaved }: { init: BuilderInit; modelRef: ModelRef; onCancel: () => void; onSaved: () => void }): React.JSX.Element {
  const [name, setName] = useState(init.name);
  const [reports, setReports] = useState<DraftReport[]>(init.reports.length ? init.reports : [{ title: '', question: '' }]);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  // Non-null while the "some reports failed" dialog is open.
  const [failures, setFailures] = useState<BuilderFailure[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const setRow = (i: number, patch: Partial<DraftReport>) => setReports((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () => setReports((rs) => [...rs, { title: '', question: '' }]);
  const removeRow = (i: number) => setReports((rs) => rs.filter((_, j) => j !== i));
  const move = (i: number, dir: -1 | 1) =>
    setReports((rs) => {
      const j = i + dir;
      if (j < 0 || j >= rs.length) return rs;
      const next = [...rs];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });

  // The questions this template already had, by id — so we can tell a report that
  // was saved (and already validated) from one that is new or has been edited.
  const savedQuestions = useMemo(() => {
    const m = new Map<string, string>();
    init.reports.forEach((r) => { if (r.id) m.set(r.id, r.question.trim()); });
    return m;
  }, [init]);
  const isSaved = (r: DraftReport): boolean => Boolean(r.id) && savedQuestions.get(r.id!) === r.question.trim();
  const statusIcon = (r: DraftReport): React.ReactNode => {
    if (isSaved(r)) return <span title="Already in this report — kept as-is, not re-checked" style={savedIcon}>✓</span>;
    if (init.id) return <span title="New or edited — checked when you save" style={newIcon}>＋</span>;
    return null; // a brand-new template: every row is new, nothing to differentiate
  };

  // Run the new/edited questions live and collect those that don't produce a report
  // — a refusal, an ambiguity, or a transport failure — so the user can fix or drop
  // them before saving. Reports already saved were validated then, so they're skipped.
  const analyse = async (): Promise<BuilderFailure[]> => {
    const candidates = reports.map((r, index) => ({ r, index })).filter(({ r }) => r.question.trim() && !isSaved(r));
    const checked = await Promise.all(candidates.map(async ({ r, index }) => {
      try {
        // Validation only needs ok/not-ok — the result and narrative are discarded,
        // so skip the insights LLM call (insights:false) to keep save-time fast.
        const outcome = await askLive(r.question.trim(), modelRef, { insights: false });
        if (outcome.ok) return null;
        const reason = outcome.stage === 'clarification'
          ? `Needs clarification — ${outcome.clarification.question}`
          : (outcome.reason || 'This report could not be generated.');
        return { index, title: r.title, question: r.question, reason };
      } catch {
        return { index, title: r.title, question: r.question, reason: 'The reporting service could not run this question.' };
      }
    }));
    return checked.filter((f): f is BuilderFailure => f !== null);
  };

  const persist = async (rs: DraftReport[]) => {
    const clean = rs.filter((r) => r.question.trim());
    if (clean.length === 0) { setErr('No working reports left — add or fix at least one.'); return; }
    setSaving(true);
    setErr(null);
    try {
      if (init.id) await updateTemplate(init.id, name.trim(), clean);
      else await createTemplate(name.trim(), clean);
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
      setSaving(false);
    }
  };

  const save = async () => {
    if (!name.trim()) return setErr('A template name is required.');
    if (reports.filter((r) => r.question.trim()).length === 0) return setErr('Add at least one report question.');
    setChecking(true);
    setErr(null);
    const fails = await analyse();
    setChecking(false);
    if (fails.length) { setFailures(fails); return; } // let the user decide before saving
    await persist(reports);
  };

  // From the dialog: drop the failing rows and save the working ones.
  const removeFailingAndSave = async () => {
    const failIdx = new Set((failures ?? []).map((f) => f.index));
    const remaining = reports.filter((_, i) => !failIdx.has(i));
    setReports(remaining);
    setFailures(null);
    await persist(remaining);
  };

  const busy = saving || checking;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <h3 style={{ margin: 0, fontSize: 16 }}>{init.id ? 'Edit template' : 'New template'}</h3>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name (e.g. POS overview)" style={inputStyle} />
      <div style={{ display: 'grid', gap: 8 }}>
        <div className="muted" style={{ fontSize: 12 }}>Reports — each question renders as a widget. On save, only new or edited questions (＋) are checked; ones already saved (✓) are kept as-is.</div>
        {reports.map((r, i) => (
          <div key={i} className="card" style={{ padding: 10, display: 'grid', gap: 6 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {statusIcon(r)}
              <input value={r.title} onChange={(e) => setRow(i, { title: e.target.value })} placeholder="Title (optional)" style={{ ...inputStyle, flex: 1 }} />
              <select value={r.widget ?? ''} onChange={(e) => setRow(i, { widget: e.target.value || undefined })} style={selectStyle} title="Widget type">
                <option value="">Auto</option>
                <option value="kpi">KPI</option>
                <option value="line">Line</option>
                <option value="bar">Bar</option>
                <option value="donut">Donut</option>
                <option value="table">Table</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={r.question} onChange={(e) => setRow(i, { question: e.target.value })} placeholder="Question (e.g. total revenue by category)" style={{ ...inputStyle, flex: 1 }} />
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} style={ghostBtn} title="Move up">↑</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === reports.length - 1} style={ghostBtn} title="Move down">↓</button>
              <button type="button" onClick={() => removeRow(i)} style={dangerBtn} title="Remove">✕</button>
            </div>
          </div>
        ))}
        <button type="button" onClick={addRow} style={ghostBtn}>+ Add report</button>
      </div>
      {err ? <p style={{ color: 'var(--bad, #dc2626)', fontSize: 13, margin: 0 }}>{err}</p> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => void save()} disabled={busy} style={primaryBtn}>{checking ? 'Checking…' : saving ? 'Saving…' : 'Save template'}</button>
        <button type="button" onClick={onCancel} disabled={busy} style={ghostBtn}>Cancel</button>
      </div>

      {failures ? (
        <div style={overlayStyle} onClick={() => setFailures(null)} role="presentation">
          <div style={dialogStyle} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Reports could not be generated">
            <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Some reports couldn’t be generated</h3>
            <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
              These questions didn’t produce a report. Remove them to save the rest, or go back and reword them.
            </p>
            <div style={{ display: 'grid', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
              {failures.map((f) => (
                <div key={f.index} style={failCardStyle}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{f.title.trim() || f.question}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{f.reason}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={() => setFailures(null)} style={ghostBtn}>Back to editing</button>
              <button type="button" onClick={() => void removeFailingAndSave()} style={dangerSolidBtn}>Remove these &amp; save</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const defaultBadge: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
  background: 'color-mix(in srgb, var(--series-2) 18%, transparent)', color: 'var(--series-2)',
};
const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: 14 };
const inputStyle: React.CSSProperties = { padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--panel, transparent)', color: 'var(--text)', fontSize: 14 };
const selectStyle: React.CSSProperties = { padding: '8px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--panel, transparent)', color: 'var(--text)', fontSize: 13, flex: '0 0 96px' };
const primaryBtn: React.CSSProperties = { padding: '6px 12px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: '1px solid var(--accent, #4f46e5)', background: 'var(--accent, #4f46e5)', color: '#fff', cursor: 'pointer' };
const ghostBtn: React.CSSProperties = { padding: '6px 12px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' };
const dangerBtn: React.CSSProperties = { padding: '6px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--bad, #dc2626)', cursor: 'pointer' };
const dangerSolidBtn: React.CSSProperties = { padding: '7px 14px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: '1px solid var(--bad, #dc2626)', background: 'var(--bad, #dc2626)', color: '#fff', cursor: 'pointer' };
const overlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const dialogStyle: React.CSSProperties = { width: 'min(480px, 100%)', background: 'var(--surface, #fff)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, boxShadow: '0 12px 40px rgba(0,0,0,0.25)' };
const failCardStyle: React.CSSProperties = { padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'color-mix(in srgb, var(--bad, #dc2626) 8%, transparent)' };
const savedIcon: React.CSSProperties = { flex: '0 0 auto', color: 'var(--good, #16a34a)', fontSize: 15, fontWeight: 700, width: 16, textAlign: 'center' };
const newIcon: React.CSSProperties = { flex: '0 0 auto', color: '#d97706', fontSize: 15, fontWeight: 700, width: 16, textAlign: 'center' };
